import type { ChangeEvent, ReactNode } from 'react';

import { useEffect, useId, useRef, useState } from 'react';

import { Button, Icon, type IconName } from '~/components';
import { Field } from '~/components/Form';
import { clsx } from '~/styles/utils';

import {
  actionsStyles,
  imageShapeStyles,
  imageStyle,
  inputStyle,
  placeholderStyle,
  previewShapeStyles,
  previewStyle,
  wrapperStyle,
} from './ImageUploadField.css';

const placeholderIcons = { avatar: 'Person', logo: 'UploadFile' } as const satisfies Record<Variant, IconName>;

type Variant = 'avatar' | 'logo';

const useObjectUrl = (file: File | null | undefined) => {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!file) return setObjectUrl(null);

    const nextUrl = URL.createObjectURL(file);
    setObjectUrl(nextUrl);

    return () => URL.revokeObjectURL(nextUrl);
  }, [file]);

  return objectUrl;
};

type Props = {
  actionsLayout?: keyof typeof actionsStyles;
  deleteLabel: string;
  description?: ReactNode;
  isDeleting?: boolean;
  isUploading?: boolean;
  label: ReactNode;
  onDelete: () => void;
  onUpload: (file: File) => void;
  pendingFile?: File | null;
  uploadLabel: string;
  url: string | null;
  variant?: Variant;
};

export const ImageUploadField = ({
  actionsLayout = 'column',
  deleteLabel,
  description,
  isDeleting = false,
  isUploading = false,
  label,
  onDelete,
  onUpload,
  pendingFile,
  uploadLabel,
  url,
  variant = 'avatar',
}: Props) => {
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const pendingUrl = useObjectUrl(pendingFile);

  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) onUpload(file);

    event.target.value = '';
  };

  const previewUrl = pendingUrl ?? url;

  return (
    <div className={wrapperStyle}>
      <Field.Root>
        <Field.Label htmlFor={inputId}>{label}</Field.Label>

        {description && <Field.Description>{description}</Field.Description>}
      </Field.Root>

      <div className={clsx(previewStyle, previewShapeStyles[variant])}>
        {previewUrl ? (
          <img alt="" className={clsx(imageStyle, imageShapeStyles[variant])} src={previewUrl} />
        ) : (
          <Icon className={placeholderStyle} name={placeholderIcons[variant]} size={64} />
        )}
      </div>

      <div className={actionsStyles[actionsLayout]}>
        <Button disabled={isDeleting} icon="Add" loading={isUploading} onClick={() => inputRef.current?.click()}>
          {uploadLabel}
        </Button>

        {(url || pendingFile) && (
          <Button disabled={isUploading} emphasis="secondary" icon="Trash" loading={isDeleting} onClick={onDelete}>
            {deleteLabel}
          </Button>
        )}
      </div>

      <input accept="image/jpeg,image/png" className={inputStyle} id={inputId} onChange={onChange} ref={inputRef} type="file" />
    </div>
  );
};
